import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { EpApplicationService } from '@epicor/ux-platform';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { Spec } from 'swagger-ui-dist';
import { HelpPageService } from './help-page.service';
import { HelpPageTypes } from '../models/help-page-types';

@Injectable({
  providedIn: 'root'
})
export class SwaggerDocService implements OnDestroy {
  cache = new Map();

  constructor(
    private httpClient: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private epApplicationService: EpApplicationService,
    private helpPageService: HelpPageService
  ) {}

  ngOnDestroy(): void {
    this.cache.clear();
  }

  /***
   * Get Service swagger doc, it is cached here.
   * @param service - service name
   * @param version - v2 or v3
   * @param type - odata or methods
   */
  getServiceSwaggerDoc(service: string, version: string, type: string): Observable<Spec> {
    // check cache and load if not found
    const key = this.createKey(service, version, type);

    if (this.cache.has(key)) {
      return of(this.cache.get(key));
    } else {
      const url = this.buildServiceProtocolUrl(service, version, type);
      return this.loadSwaggerDoc(url).pipe(
        map(doc => {
          this.cache.set(key, doc);
          return doc;
        })
      );
    }
  }

  createKey(service: string, version: string, type: string): string {
    return `${service}_${version}_${type}`;
  }

  buildServiceProtocolUrl(service: string, version: string, type: string): string {
    const serverUrl = this.epApplicationService.sessionService.serverUrl;
    return `${serverUrl}/api/swagger/${version}/${type}/${service}`;
  }

  loadSwaggerDoc(url: string): Observable<Spec> {
    const token = this.epApplicationService.tokenService.token;

    // check token!
    if (!this.epApplicationService.tokenService.hasToken()) {
      return;
    }

    return this.helpPageService.getHelpPageType().pipe(
      concatMap(helpPage => {
        // new version requires explicit extension
        if (helpPage.helpPageType === HelpPageTypes.OpenAPI) {
          url += '.json';
        }
        return this.httpClient.get(url, { headers: { Authorization: 'Bearer ' + token } });
      })
    );
  }
}
